import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';

const HistoryTable = ({ userID, collectionName, historyType, title, columns, searchField, isTopLevelCollection }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Build the backend URL for this table. isTopLevelCollection='admin_send_logs'
  // → the admin send-log view; otherwise the caller's own send/receive history.
  const buildUrl = useCallback((skip, term) => {
    const params = new URLSearchParams({ limit: String(itemsPerPage), skip: String(skip) });
    if (term) params.set('q', term);
    if (isTopLevelCollection) return `/api/transaction/admin-send-logs?${params.toString()}`;
    const direction = historyType === 'sendHistory' ? 'send' : 'receive';
    params.set('direction', direction);
    return `/api/account/my-history?${params.toString()}`;
  }, [itemsPerPage, isTopLevelCollection, historyType]);

  const fetchHistory = useCallback(async (loadMore = false, term = '') => {
    if (!isTopLevelCollection && !userID) { setLoading(false); return; }
    if (term && searchField && isNaN(parseInt(term, 10))) {
      setHistory([]); setHasMore(false); setLoading(false); setIsSearching(false); return;
    }
    if (loadMore) { setLoadingMore(true); } else { setLoading(true); }
    setError(null);
    try {
      const skip = loadMore ? history.length : 0;
      const rows = await apiGet(buildUrl(skip, term));
      setHasMore(rows.length === itemsPerPage);
      if (loadMore) { setHistory(prev => [...prev, ...rows]); } else { setHistory(rows); }
    } catch (err) {
      setError('Could not fetch history. Please try again later.');
    } finally { setLoading(false); setLoadingMore(false); setIsSearching(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, isTopLevelCollection, searchField, itemsPerPage, buildUrl, history.length]);

  useEffect(() => {
    if (isTopLevelCollection || userID) fetchHistory(false, searchTerm.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, userID, collectionName, historyType, isTopLevelCollection]);

  const handleSearch = (e) => {
    e.preventDefault();
    const term = e.target.elements.search.value;
    if (term === searchTerm) return;
    setIsSearching(true);
    setSearchTerm(term);
  };

  const clearSearch = () => {
    const searchInput = document.querySelector('input[name="search"]');
    if (searchInput) searchInput.value = '';
    setSearchTerm('');
  };

  // Skeleton rows keep the table at its final height while loading, so the page
  // doesn't jump when the data lands.
  const renderSkeleton = () => Array.from({ length: 5 }).map((_, i) => (
    <tr key={i}>
      {columns.map(col => (
        <td key={col.key}>
          <div className="h-3.5 rounded bg-ink-500/60 animate-pulse" />
        </td>
      ))}
    </tr>
  ));

  const renderCell = (col, entry) => {
    if (col.key === 'date') {
      return (
        <span className="text-slate-400 text-xs">
          {entry.date ? new Date(entry.date.seconds ? entry.date.seconds * 1000 : entry.date).toLocaleString() : '—'}
        </span>
      );
    }
    if (col.key === 'amount') {
      return <span className="font-bold text-emerald-400 tabular-nums">{entry.amount?.toFixed(2)}</span>;
    }
    if (col.key === 'receiverId' || col.key === 'senderId' || col.key === 'receiverNumericId') {
      return <span className="font-mono text-xs text-slate-400">{entry[col.key] || '—'}</span>;
    }
    if (col.key === 'adminUid') {
      return <span className="font-mono text-2xs text-slate-600">{entry.adminUid || '—'}</span>;
    }
    return <span className="text-slate-400">{entry[col.key] ?? '—'}</span>;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>

        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              name="search"
              placeholder={`Search by ${(searchField || '').replace('Id', ' ID')}`}
              className="ui-input py-2 pr-10 text-xs min-w-[210px]"
            />
            <button
              type="submit"
              disabled={isSearching}
              className="absolute right-1 top-1/2 -translate-y-1/2 px-2.5 py-1.5 text-slate-500 hover:text-accent transition-colors"
              aria-label="Search"
            >
              <i className={`fas ${isSearching ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'} text-xs`} />
            </button>
          </form>
          {searchTerm && (
            <button type="button" onClick={clearSearch} className="ui-btn-ghost px-3 py-2" aria-label="Clear search">
              <i className="fas fa-xmark text-xs" />
            </button>
          )}
        </div>
      </div>

      {error && <div className="ui-error"><i className="fas fa-circle-exclamation" />{error}</div>}

      <div className="ui-table-wrap">
        <table className="ui-table">
          <thead>
            <tr>{columns.map(col => <th key={col.key}>{col.label}</th>)}</tr>
          </thead>
          <tbody>
            {loading ? renderSkeleton() : history.length > 0 ? (
              history.map(entry => (
                <tr key={entry.id}>
                  {columns.map(col => <td key={col.key}>{renderCell(col, entry)}</td>)}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className="ui-empty">
                    <i className="fas fa-inbox text-2xl text-slate-700" />
                    <p className="text-sm text-slate-500">
                      {searchTerm ? 'No records match that search.' : 'No records yet.'}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!searchTerm && hasMore && history.length > 0 && (
        <div className="text-center">
          <button onClick={() => fetchHistory(true, searchTerm)} disabled={loadingMore} className="ui-btn-ghost text-xs py-2">
            {loadingMore ? <><span className="ui-spinner w-3.5 h-3.5" /> Loading…</> : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default HistoryTable;
